import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "next-i18next";
import { useState } from "react";
import useCheckoutAddons, {
  DEFAULT_PRODUCT_SORT,
} from "@app-contexts/public/shared/checkout/hooks/useCheckoutAddons";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import {
  usePublicBasket,
  UsePublicBasketType,
} from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { PublicVenueSerializer } from "@app-contexts/dashboard/features/venues/data/types/venues";
import { NestedPublicVenueSerializer } from "@shared/modules/events/types/public";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import Swiper from "../Swiper.web";
import ProductAddonsModal from "./ProductAddonsModal";
import { AddonProductCard } from "./AddonProductCard";
import { PAGE_SIZE, ProductAddonGrid } from "./ProductAddonGrid";

export default function ProductAddons({
  modalHeight,
}: {
  modalHeight?: string;
}): JSX.Element {
  const { t } = useTranslation("checkout");
  const { isFlagActive } = useDjangoWaffle();
  const publicBasket = usePublicBasket({
    purchase_source_platform: getEmbeddedPurchasePlatform(),
  });
  const renderAsGrid = isFlagActive(
    "product_addons_render_as_grid",
    publicBasket.paymentVenue
  );

  const {
    products,
    isProductsLoading,
    hasNextPageProducts,
    isProductsFetchingNextPage,
    fetchNextPageProducts,
  } = useCheckoutAddons({
    productPageSize: renderAsGrid ? PAGE_SIZE : 10,
    productSort: DEFAULT_PRODUCT_SORT,
  });

  const { useRetrieve } = usePublicVenueService();
  const { data: venue } = useRetrieve(publicBasket.paymentVenue);

  if (products.length === 0 && !isProductsLoading) {
    return <></>;
  }

  return (
    <Flex pt="lg" pb="lg" direction="column" bg="bg">
      <Flex gap="2xs" px={{ base: "lg", sm: "xl" }} alignItems="center">
        <Text
          fontSize="xl"
          fontWeight="bold"
          data-testid="addons-products-heading"
        >
          {t("Add-ons")}
        </Text>
        <Text fontSize="md" ml="2xs" color="gray.600" mt="2xs">
          ({t("Optional")})
        </Text>
      </Flex>
      <Flex
        direction="column"
        gap="md"
        pr={renderAsGrid ? "20px" : "0px"}
        pl={{ base: renderAsGrid ? "20px" : "0px", md: "lg" }}
      >
        {(products?.length > 0 || isProductsLoading) && (
          <ProductAddons.Products
            venue={venue}
            isLoading={isProductsLoading || isProductsFetchingNextPage}
            renderAsGrid={renderAsGrid}
            showAll={renderAsGrid}
            products={products ?? []}
            publicBasket={publicBasket}
            modalHeight={modalHeight}
            hasMore={hasNextPageProducts}
            onLoadMore={fetchNextPageProducts}
          />
        )}
      </Flex>
    </Flex>
  );
}

ProductAddons.Products = function Products({
  products,
  publicBasket,
  modalHeight,
  renderAsGrid,
  isLoading,
  venue,
  showAll,
  hasMore,
  onLoadMore,
}: {
  products: ProductTypeSerializer[];
  publicBasket: UsePublicBasketType;
  modalHeight?: string;
  renderAsGrid?: boolean;
  isLoading?: boolean;
  venue?: PublicVenueSerializer | NestedPublicVenueSerializer;
  showAll?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}): JSX.Element {
  const {
    isOpen: isAddonProductModalOpen,
    onOpen: onOpenAddonProductModal,
    onClose: onCloseAddonProductModal,
  } = useDisclosure({ defaultIsOpen: false });

  const [selectedProduct, setSelectedProduct] =
    useState<ProductTypeSerializer | null>(null);

  return (
    <>
      {(products.length > 0 || isLoading) && (
        <Track.Impression
          trackOnce
          analytics={{
            label: "addons",
            custom_params: {
              datum: "list_view",
              type: "products",
              count: products.length,
            },
          }}
        >
          <Flex w="100%">
            {renderAsGrid ? (
              <Box pt="lg">
                <ProductAddonGrid
                  isLoading={isLoading}
                  products={products}
                  allInPricing={Boolean(venue?.all_in_pricing_is_enabled)}
                  onOpenAddonProductModal={onOpenAddonProductModal}
                  setSelectedProduct={setSelectedProduct}
                  showAll={showAll}
                  hasMore={hasMore}
                  onLoadMore={onLoadMore}
                />
              </Box>
            ) : (
              products.length > 0 && (
                <Swiper slidesOffsetBefore={20}>
                  {products.map((product) => (
                    <Swiper.Slide key={product.id}>
                      <AddonProductCard
                        product={product}
                        publicBasket={publicBasket}
                        fitToParent={renderAsGrid}
                        allInPricing={Boolean(venue?.all_in_pricing_is_enabled)}
                        openAddonProductModal={onOpenAddonProductModal}
                        setSelectedProduct={setSelectedProduct}
                      />
                    </Swiper.Slide>
                  ))}
                </Swiper>
              )
            )}
          </Flex>
        </Track.Impression>
      )}

      {selectedProduct && (
        <ProductAddonsModal
          modalHeight={modalHeight}
          isOpen={isAddonProductModalOpen}
          onClose={() => {
            onCloseAddonProductModal();
            setSelectedProduct(null);
          }}
          product={selectedProduct}
        />
      )}
    </>
  );
};
